import React, { useState, useEffect } from 'react';
import { Plus, Trash, Calendar, X, CheckCircle, Eye } from '@phosphor-icons/react';
import GenericTables from '../../../shared/components/ui/GenericTables.jsx';
import GenericFilters from '../../../shared/components/ui/GenericFilters.jsx';
import Alerts from '../../../shared/components/ui/Alerts.jsx';
import { getUserFromToken } from '../../../context/authContext.jsx';
import { AuthContext } from '../../../context/authContext.jsx';
import { useContext } from 'react';
import { Navigate } from 'react-router-dom';
import { teamDisableService } from '../services/teamDisableService.js';
import { teamService } from '../../../services/teams/teamService.js';
import PageHeader from '../../../shared/components/ui/PageHeader.jsx';
import DateTimePickerField from '../../../shared/components/ui/DateTimePickerField.jsx';

const parseLocalDateInput = (value) => {
  if (!value || typeof value !== 'string') return null;

  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return null;

  return new Date(year, month - 1, day);
};

const getStartOfToday = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
};

const isCurrentOrFutureDateInput = (value) => {
  const parsedDate = parseLocalDateInput(value);
  return parsedDate ? parsedDate >= getStartOfToday() : false;
};

const TeamDisableManagement = () => {
  const [disables, setDisables] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedDisable, setSelectedDisable] = useState(null);
  const [filters, setFilters] = useState({
    team: '',
    date: ''
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });
  const {isAuthenticated} = useContext(AuthContext)
  const [modalError, setModalError] = useState('');

  // Formulario de creación
  const [formData, setFormData] = useState({
    teamId: '',
    date: '',
    reason: ''
  });

  // Detectar rol del usuario
  useEffect(() => {
    const user = getUserFromToken();
    if (user && user.role) {
      setUserRole(user.role);
    }
  }, []);

  // Cargar equipos
  useEffect(() => {
    fetchTeams();
  }, []);

  // Cargar inhabilitaciones
  useEffect(() => {
    fetchDisables();
  }, [filters.team, filters.date, currentPage, pageSize]);

  const fetchTeams = async () => {
    try {
      const data = await teamService.getTeams();
      setTeams(data || []);
    } catch (err) {
      console.error('Error al cargar equipos:', err);
      setTeams([]);
    }
  };

  const fetchDisables = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await teamDisableService.getDisables({
        teamId: filters.team,
        date: filters.date,
        pageSize,
        pageCurrent: currentPage - 1
      });
      const data = response.disables || [];

      setDisables(data);

      if (response.pagination) {
        const responseTotalItems = Number(response.pagination.totalItems) || 0;
        const responsePageSize = Number(response.pagination.pageSize) || pageSize;

        setTotalItems(responseTotalItems);
        setTotalPages(responseTotalItems > 0 ? Math.ceil(responseTotalItems / responsePageSize) : 1);
      } else {
        setTotalItems(sortedData.length);
        setTotalPages(1);
      }
    } catch (err) {
      console.error('Error al cargar inhabilitaciones:', err);
      setError('Error al cargar las inhabilitaciones');
      setDisables([]);
      setTotalItems(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  };

  const handleCreateDisable = async () => {
    try {
      // Validaciones
      if (!formData.teamId) {
        setModalError('El equipo es requerido');
        return;
      }

      if (!formData.date) {
        setModalError('La fecha es requerida');
        return;
      }

      // Validar que la fecha no sea pasada
      const selectedDate = parseLocalDateInput(formData.date);
      if (!selectedDate) {
        setModalError('La fecha es invalida');
        return;
      }

      const today = getStartOfToday();
      
      if (selectedDate < today) {
        setModalError('No se puede inhabilitar un equipo en una fecha pasada');
        return;
      }

      const payload = {
        teamId: formData.teamId,
        date: formData.date,
        ...(formData.reason && { reason: formData.reason })
      };

      await teamDisableService.createDisable(payload);

      showNotification('Inhabilitación creada correctamente', 'success');
      setShowCreateModal(false);
      setFormData({ teamId: '', date: '', reason: '' });
      setModalError('');
      fetchDisables();
    } catch (err) {
      console.error('Error al crear inhabilitación:', err);
      const errorMessage = err.message?.includes('Ya existe') 
        ? 'Ya existe una inhabilitación para este equipo en la fecha especificada'
        : 'Error al crear la inhabilitación';
      setModalError(errorMessage);
    }
  };

  const handleDeleteDisable = async () => {
    if (!selectedDisable) return;

    try {
      await teamDisableService.deleteDisable({
        teamId: selectedDisable.teamId,
        date: selectedDisable.date
      });

      showNotification('Inhabilitación eliminada correctamente', 'success');
      setShowDeleteConfirm(false);
      setSelectedDisable(null);
      fetchDisables();
    } catch (err) {
      console.error('Error al eliminar inhabilitación:', err);
      showNotification('Error al eliminar la inhabilitación', 'error');
    }
  };

  const openCreateModal = () => {
    setFormData({ teamId: '', date: '', reason: '' });
    setModalError('');
    setShowCreateModal(true);
  };

  const showNotification = (message, type = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: '', type: 'success' });
    }, 3000);
  };

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const isAdmin = userRole === 'admin';

  // Configuración de columnas para la tabla
  const columns = [
    {
      key: 'team',
      label: 'Equipo',
      align: 'center',
      render: (row) => row.team?.name || 'N/A'
    },
    {
      key: 'disableDate',
      label: 'Fecha de Inhabilitación',
      align: 'center',
      render: (row) =>  row.date
    },
    {
      key: 'reason',
      label: 'Motivo',
      align: 'center',
      render: (row) => {
        const maxLength = 15;
        const reason = row.reason || '';
        if (!reason) {
          return <span className="text-content-muted italic">Sin motivo</span>;
        }
        if (reason.length <= maxLength) {
          return <span>{reason}</span>;
        }
        return <span>{reason.substring(0, maxLength)}...</span>;
      }
    },
    {
      key: 'createdAt',
      label: 'Fecha de Creación',
      align: 'center',
      render: (row) => row.createdAt.split('T')[0]
    },
    {
      key: 'actions',
      label: 'Acciones',
      align: 'center',
      isAction: true,
      render: (row) => (
        <div className="flex items-center justify-center gap-2">
          <button
            onClick={() => {
              setSelectedDisable(row);
              setShowDetailsModal(true);
            }}
            className="p-2 rounded bg-surface-2 hover:bg-surface text-content transition-colors"
            title="Ver detalles completos"
          >
            <Eye size={18} weight="bold" />
          </button>
          {isAdmin && isCurrentOrFutureDateInput(row.date) && (
            <button
              onClick={() => {
                setSelectedDisable(row);
                setShowDeleteConfirm(true);
              }}
              className="p-2 rounded bg-error hover:opacity-90 text-white transition-colors"
              title="Eliminar inhabilitación"
            >
              <Trash size={18} weight="bold" />
            </button>
          )}
        </div>
      )
    }
  ];

  // Configuración de filtros
  const filterColumns = [
    {
      type: 'select',
      key: 'team',
      label: 'Equipo',
      options: [{ id: '', name: 'Todos los equipos' }, ...teams],
      optionValue: 'id',
      optionLabel: 'name',
      colSpan: 1
    },
    {
      type: 'date',
      key: 'date',
      label: 'Fecha',
      colSpan: 1
    }
  ];

  if (!isAuthenticated) {
    return (
      <div className="container">
            <Navigate to="/admin/login" replace/>
      </div>
    )
  }
  return (
    <div className="min-h-screen bg-bg">
      <PageHeader
				title="Inhabilitaciones"
				icon={<Calendar size={28} weight="duotone" />}
				subtitle="Administra las fechas en las que los equipos no estarán disponibles para asignaciones"
				actionButton={{
          show: isAdmin,
          label: 'Crear Inhabilitación',
          icon: <Plus size={18} />,
          onClick: openCreateModal,
          className: 'bg-amber hover:bg-amber-hover text-on-amber px-4 py-2 rounded-lg'
        }}
			/>
     


      {/* Filtros */}
      <GenericFilters
        columns={filterColumns}
        filters={filters}
        onFilterChange={(nextFilters) => {
          setFilters(nextFilters);
          setCurrentPage(1);
        }}
        onClearFilters={() => {
          setFilters({ team: '', date: '' });
          setCurrentPage(1);
        }}
        defaultOpen={false}
      />

      {/* Tabla de inhabilitaciones */}
      {loading && disables.length === 0 ? (
        <div className="bg-surface rounded-xl shadow-lg border border-line p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber mx-auto"></div>
          <p className="mt-2 text-content-muted">Cargando inhabilitaciones...</p>
        </div>
      ) : error ? (
        <div className="bg-surface rounded-xl shadow-lg border border-line p-8 text-center">
          <p className="text-error mb-4">{error}</p>
          <button
            onClick={fetchDisables}
            className="bg-error hover:opacity-90 text-white px-4 py-2 rounded"
          >
            Reintentar
          </button>
        </div>
      ) : (
        <GenericTables
          columns={columns}
          data={disables}
          emptyMessage="No hay inhabilitaciones registradas"
          emptyColSpan={columns.length}
          showFooter
          pagination={{
            currentPage,
            totalPages,
            pageSize,
            totalItems,
            pageSizeOptions: [5, 10, 20, 50],
            onPageChange: handlePageChange,
            onPageSizeChange: handlePageSizeChange,
          }}
        />
      )}

      {/* Modal de creación */}
      {showCreateModal && (
        <Alerts
          title="Crear Inhabilitación"
          onClose={() => {
            setShowCreateModal(false);
            setFormData({ teamId: '', date: '', reason: '' });
            setModalError('');
          }}
        >
          <div className="space-y-4">
            {/* Mensaje de error dentro del modal */}
            {modalError && (
              <div className="mb-4 p-4 rounded-lg bg-error/12 border border-error text-error flex items-center gap-2">
                <X size={20} weight="bold" />
                <span>{modalError}</span>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-content-muted mb-2">
                Equipo <span className="text-error">*</span>
              </label>
              <select
                value={formData.teamId}
                onChange={(e) => {
                  setFormData({ ...formData, teamId: e.target.value });
                  setModalError(''); // Limpiar error al cambiar el campo
                }}
                className="w-full px-3 py-2 text-sm border border-line rounded-lg bg-surface-2 text-content focus:outline-none focus:ring-2 focus:ring-amber"
                required
              >
                <option value="">Selecciona un equipo</option>
                {teams.map((team) => (
                  <option key={team.id} value={team.id}>
                    {team.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-content-muted mb-2">
                Fecha <span className="text-error">*</span>
              </label>
              <DateTimePickerField
                value={formData.date}
                onChange={(value) => {
                  setFormData({ ...formData, date: value });
                  setModalError(''); // Limpiar error al cambiar el campo
                }}
                mode="date"
                minDate={getStartOfToday()}
                placeholder="dd/mm/aaaa"
                buttonClassName="h-12 rounded-md bg-surface-2"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-content-muted mb-2">
                Motivo (Opcional)
              </label>
              <textarea
                value={formData.reason}
                onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                placeholder="Ej: Mantenimiento programado, Día festivo, etc."
                rows={3}
                maxLength={255}
                className="w-full px-3 py-2 text-sm border border-line rounded-lg bg-surface-2 text-content focus:outline-none focus:ring-2 focus:ring-amber resize-none"
              />
            </div>

            <div className="flex gap-3 pt-4">
              <button
                onClick={handleCreateDisable}
                className="flex-1 bg-amber hover:bg-amber-hover text-on-amber px-4 py-2 rounded-lg transition-colors"
              >
                Crear Inhabilitación
              </button>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setFormData({ teamId: '', date: '', reason: '' });
                }}
                className="flex-1 bg-surface-2 hover:bg-surface text-content px-4 py-2 rounded-lg transition-colors"
              >
                Cancelar
              </button>
            </div>
          </div>
        </Alerts>
      )}

      {/* Modal de detalles completos */}
      {showDetailsModal && selectedDisable && (
        <Alerts
          title="Detalles de la Inhabilitación"
          onClose={() => {
            setShowDetailsModal(false);
            setSelectedDisable(null);
          }}
        >
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-surface-2 p-4 rounded-lg">
                <h4 className="text-sm font-semibold text-content-muted mb-2">Equipo</h4>
                <p className="text-content">{selectedDisable.team?.name || 'N/A'}</p>
                {selectedDisable.team?.description && (
                  <p className="text-sm text-content-muted mt-1">{selectedDisable.team.description}</p>
                )}
              </div>

              <div className="bg-surface-2 p-4 rounded-lg">
                <h4 className="text-sm font-semibold text-content-muted mb-2">Fecha de Inhabilitación</h4>
                <p className="text-content">{selectedDisable.date}</p>
              </div>

              <div className="bg-surface-2 p-4 rounded-lg md:col-span-2">
                <h4 className="text-sm font-semibold text-content-muted mb-2">Motivo</h4>
                <p className="text-content whitespace-pre-wrap break-words">
                  {selectedDisable.reason || <span className="text-content-muted italic">Sin motivo especificado</span>}
                </p>
              </div>

              <div className="bg-surface-2 p-4 rounded-lg">
                <h4 className="text-sm font-semibold text-content-muted mb-2">Fecha de Creación</h4>
                <p className="text-content">
                  {selectedDisable.createdAt
                    ? new Date(selectedDisable.createdAt).toLocaleString('es-AR', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })
                    : 'N/A'}
                </p>
              </div>

              {selectedDisable.updatedAt && selectedDisable.updatedAt !== selectedDisable.createdAt && (
                <div className="bg-surface-2 p-4 rounded-lg">
                  <h4 className="text-sm font-semibold text-content-muted mb-2">Última Actualización</h4>
                  <p className="text-content">
                    {new Date(selectedDisable.updatedAt).toLocaleString('es-AR', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                </div>
              )}
            </div>

            <div className="flex gap-3 pt-4">
              {isAdmin && isCurrentOrFutureDateInput(selectedDisable.date) && (
                <button
                  onClick={() => {
                    setShowDetailsModal(false);
                    setShowDeleteConfirm(true);
                  }}
                  className="flex-1 bg-error hover:opacity-90 text-white px-4 py-2 rounded-lg transition-colors"
                >
                  Eliminar Inhabilitación
                </button>
              )}
            </div>
          </div>
        </Alerts>
      )}

      {/* Modal de confirmación de eliminación */}
      {showDeleteConfirm && selectedDisable && (
        <Alerts
          title="Confirmar Eliminación"
          onClose={() => {
            setShowDeleteConfirm(false);
            setSelectedDisable(null);
          }}
        >
          <div className="space-y-4">
            <p className="text-content-muted">
              ¿Estás seguro de que quieres eliminar la inhabilitación del {' '}
              <strong className="text-content">
                {selectedDisable.team?.name || 'N/A'}
              </strong>{' '}
              para la fecha{' '}
              <strong className="text-content">
                {selectedDisable.date}
              </strong>?
            </p>
            {selectedDisable.reason && (
              <p className="text-sm text-content-muted">
                Razón: {selectedDisable.reason}
              </p>
            )}
            <div className="flex gap-3 pt-4">
              <button
                onClick={handleDeleteDisable}
                className="flex-1 bg-error hover:opacity-90 text-white px-4 py-2 rounded-lg transition-colors"
              >
                Eliminar
              </button>
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setSelectedDisable(null);
                }}
                className="flex-1 bg-surface-2 hover:bg-surface text-content px-4 py-2 rounded-lg transition-colors"
              >
                Cancelar
              </button>
            </div>
          </div>
        </Alerts>
      )}

      {/* Notificación */}
      {notification.show && (
        <div
          className={`fixed top-4 right-4 z-10000 p-4 rounded-lg shadow-lg ${
            notification.type === 'success'
              ? 'bg-success/12 border border-success text-success'
              : 'bg-error/12 border border-error text-error'
          }`}
        >
          <div className="flex items-center gap-2">
            {notification.type === 'success' ? (
              <CheckCircle size={20} weight="bold" />
            ) : (
              <X size={20} weight="bold" />
            )}
            <span>{notification.message}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamDisableManagement;
